/**
 * ClientsCacheContext
 *
 * Route-scoped via ClientsCacheLayout (/dashboard, /clients list only).
 *  1. Polls /api/protected/clients + summary once every 60s while mounted
 *  2. On-demand detail via getClientDetail(clientId) — assessments + posture per client
 *
 * Only dashboard + clients list may import useClientsCache.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react';
import type { Client, ClientSummary, Assessment, PostureAnalysis } from '@/lib/types';
import { subscribeClients, subscribeClientsSummary } from '@/components/clients/services/clients-api';
import { getClientAssessments } from '@/components/sop-intake/services/assessments-api';
import { getClientPostureAnalyses } from '@/components/posture-analysis/services/posture-analyses-api';
import { useAuth } from '@/contexts/AuthContext';
import { useAbility, useAbilityLoading } from '@/components/authnz/AbilityContext';
import {
  computeClientIdsKey,
  isClientsCacheAuthError,
  isClientsCachePermissionError,
} from '@/lib/clients/cache-routes';
import type {
  CachedAssessment,
  CachedPostureAnalysis,
  ClientDetailCache,
} from '@/lib/clients/types';
import logger from '@/lib/logging';

export type { CachedAssessment, CachedPostureAnalysis } from '@/lib/clients/types';

export interface ClientDetail extends ClientDetailCache {}

interface ClientsCacheContextType {
  clients: Client[];
  clientsFetchError: string | null;
  loading: boolean;
  clientSummaries: ClientSummary[];
  summaryLoading: boolean;
  summaryFetchError: string | null;
  getClientDetail: (clientId: string) => Promise<ClientDetail>;
  detailVersion: number;
  refresh: () => void;
}

const ClientsCacheContext = createContext<ClientsCacheContextType | undefined>(undefined);

const POLL_INTERVAL_MS = 60_000;

export function ClientsCacheProvider({ children }: { children: ReactNode }) {
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsFetchError, setClientsFetchError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [clientSummaries, setClientSummaries] = useState<ClientSummary[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryFetchError, setSummaryFetchError] = useState<string | null>(null);
  const [detailVersion, setDetailVersion] = useState(0);

  const { loading: authLoading } = useAuth();
  const ability = useAbility();
  const abilityLoading = useAbilityLoading();
  const canReadClients = ability.can('read', 'Client');

  const [refreshTick, setRefreshTick] = useState(0);
  const clientsRef = useRef<Client[]>([]);
  clientsRef.current = clients;

  const clientIdsKey = useMemo(() => computeClientIdsKey(clients), [clients]);

  const subscribedRef = useRef(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const summarySubscribedRef = useRef(false);
  const summaryUnsubscribeRef = useRef<(() => void) | null>(null);
  const [pollPausedForPermission, setPollPausedForPermission] = useState(false);
  const [summaryPollPausedForPermission, setSummaryPollPausedForPermission] = useState(false);

  const assessmentsByClientRef = useRef<Map<string, CachedAssessment[]>>(new Map());
  const analysesByClientRef = useRef<Map<string, CachedPostureAnalysis[]>>(new Map());
  const detailInflightRef = useRef<Map<string, Promise<ClientDetail>>>(new Map());

  const stopClientsSubscription = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    subscribedRef.current = false;
  }, []);

  const stopSummarySubscription = useCallback(() => {
    if (summaryUnsubscribeRef.current) {
      summaryUnsubscribeRef.current();
      summaryUnsubscribeRef.current = null;
    }
    summarySubscribedRef.current = false;
  }, []);

  const pruneRemovedClients = useCallback((clientIdsKey: string) => {
    const activeIds = new Set(clientIdsKey ? clientIdsKey.split(',') : []);
    for (const id of assessmentsByClientRef.current.keys()) {
      if (!activeIds.has(id)) {
        assessmentsByClientRef.current.delete(id);
        analysesByClientRef.current.delete(id);
      }
    }
  }, []);

  // ─── Client list polling ───────────────────────────────────────────────────
  useEffect(() => {
    if (authLoading || abilityLoading) return;

    if (!canReadClients) {
      stopClientsSubscription();
      stopSummarySubscription();
      setLoading(false);
      setSummaryLoading(false);
      return;
    }

    if (pollPausedForPermission) {
      return;
    }

    if (subscribedRef.current) return;
    subscribedRef.current = true;

    logger.debug('[ClientsCache] Starting route-scoped clients subscription', {
      category: 'poll',
      component: 'ClientsCacheProvider',
      intervalMs: POLL_INTERVAL_MS,
    });

    const unsubscribe = subscribeClients(
      (data) => {
        setClients(data);
        setClientsFetchError(null);
        setLoading(false);
        setPollPausedForPermission(false);
      },
      POLL_INTERVAL_MS,
      (error) => {
        if (isClientsCacheAuthError(error)) {
          stopClientsSubscription();
          setClientsFetchError(null);
          setLoading(true);
          logger.debug('[ClientsCache] Session credential rejected — leaving roster to session recovery', {
            category: 'poll',
          });
          return;
        }
        if (isClientsCachePermissionError(error)) {
          setPollPausedForPermission(true);
          stopClientsSubscription();
          logger.debug('[ClientsCache] Permission denied — pausing client poll until refresh', {
            category: 'poll',
          });
        } else {
          logger.error('[ClientsCache] Error fetching clients:', error);
        }
        setClientsFetchError(
          error instanceof Error ? error.message : 'Could not load clients. Please try again.'
        );
        setLoading(false);
      }
    );
    unsubscribeRef.current = unsubscribe;

    return () => {
      logger.debug('[ClientsCache] Stopping clients subscription', {
        category: 'poll',
        component: 'ClientsCacheProvider',
      });
      stopClientsSubscription();
    };
  }, [
    authLoading,
    abilityLoading,
    canReadClients,
    refreshTick,
    pollPausedForPermission,
    stopClientsSubscription,
  ]);

  // ─── Client summary polling ────────────────────────────────────────────────
  useEffect(() => {
    if (authLoading || abilityLoading) return;

    if (!canReadClients) {
      stopSummarySubscription();
      setSummaryLoading(false);
      return;
    }

    if (summaryPollPausedForPermission) {
      return;
    }

    if (summarySubscribedRef.current) return;
    summarySubscribedRef.current = true;

    logger.debug('[ClientsCache] Starting clients summary subscription', {
      category: 'poll',
      component: 'ClientsCacheProvider',
      intervalMs: POLL_INTERVAL_MS,
    });

    const unsubscribe = subscribeClientsSummary(
      (data) => {
        setClientSummaries(data);
        setSummaryFetchError(null);
        setSummaryLoading(false);
        setSummaryPollPausedForPermission(false);
      },
      POLL_INTERVAL_MS,
      (error) => {
        if (isClientsCacheAuthError(error)) {
          stopSummarySubscription();
          setSummaryFetchError(null);
          setSummaryLoading(false);
          logger.debug('[ClientsCache] Session credential rejected — skipping activity-flag banner', {
            category: 'poll',
          });
          return;
        }
        if (isClientsCachePermissionError(error)) {
          setSummaryPollPausedForPermission(true);
          stopSummarySubscription();
          logger.debug('[ClientsCache] Permission denied — pausing summary poll until refresh', {
            category: 'poll',
          });
        } else {
          logger.error('[ClientsCache] Error fetching clients summary:', error);
        }
        setSummaryFetchError(
          error instanceof Error ? error.message : 'Could not load client summaries. Please try again.'
        );
        setSummaryLoading(false);
      }
    );
    summaryUnsubscribeRef.current = unsubscribe;

    return () => {
      logger.debug('[ClientsCache] Stopping clients summary subscription', {
        category: 'poll',
        component: 'ClientsCacheProvider',
      });
      stopSummarySubscription();
    };
  }, [
    authLoading,
    abilityLoading,
    canReadClients,
    refreshTick,
    summaryPollPausedForPermission,
    stopSummarySubscription,
  ]);

  // Resume polling after session/ability stabilizes following a 403
  useEffect(() => {
    if (!authLoading && !abilityLoading && canReadClients && pollPausedForPermission) {
      setPollPausedForPermission(false);
      subscribedRef.current = false;
      setRefreshTick((t) => t + 1);
    }
  }, [authLoading, abilityLoading, canReadClients, pollPausedForPermission]);

  useEffect(() => {
    if (!authLoading && !abilityLoading && canReadClients && summaryPollPausedForPermission) {
      setSummaryPollPausedForPermission(false);
      summarySubscribedRef.current = false;
      setRefreshTick((t) => t + 1);
    }
  }, [authLoading, abilityLoading, canReadClients, summaryPollPausedForPermission]);

  // Evict detail cache for clients no longer in roster
  useEffect(() => {
    if (!clientIdsKey) {
      assessmentsByClientRef.current.clear();
      analysesByClientRef.current.clear();
      detailInflightRef.current.clear();
      return;
    }
    pruneRemovedClients(clientIdsKey);
  }, [clientIdsKey, pruneRemovedClients]);

  const getClientDetail = useCallback(
    async (clientId: string): Promise<ClientDetail> => {
      const activeIds = new Set(clientIdsKey ? clientIdsKey.split(',') : []);
      if (!activeIds.has(clientId)) {
        return { assessments: [], postureAnalyses: [] };
      }

      if (assessmentsByClientRef.current.has(clientId)) {
        return {
          assessments: assessmentsByClientRef.current.get(clientId) ?? [],
          postureAnalyses: analysesByClientRef.current.get(clientId) ?? [],
        };
      }

      const inflight = detailInflightRef.current.get(clientId);
      if (inflight) {
        return inflight;
      }

      const promise = (async (): Promise<ClientDetail> => {
        try {
          const [assessments, analyses] = await Promise.all([
            getClientAssessments(clientId),
            getClientPostureAnalyses(clientId),
          ]);
          const cachedAssessments = assessments.map((a) => ({ ...a, clientId }));
          const cachedAnalyses = analyses.map((a) => ({ ...a, clientId }));
          assessmentsByClientRef.current.set(clientId, cachedAssessments);
          analysesByClientRef.current.set(clientId, cachedAnalyses);
          setDetailVersion((v) => v + 1);
          logger.debug('[ClientsCache] On-demand detail fetched', {
            category: 'fetch',
            component: 'ClientsCacheProvider',
            clientId,
          });
          return { assessments: cachedAssessments, postureAnalyses: cachedAnalyses };
        } catch (error) {
          logger.error(`[ClientsCache] Error fetching detail for client ${clientId}:`, error);
          assessmentsByClientRef.current.set(clientId, []);
          analysesByClientRef.current.set(clientId, []);
          setDetailVersion((v) => v + 1);
          return { assessments: [], postureAnalyses: [] };
        } finally {
          detailInflightRef.current.delete(clientId);
        }
      })();

      detailInflightRef.current.set(clientId, promise);
      return promise;
    },
    [clientIdsKey]
  );

  const refresh = useCallback(() => {
    logger.debug('[ClientsCache] Manual refresh requested', { category: 'poll' });
    setClientsFetchError(null);
    setSummaryFetchError(null);
    setPollPausedForPermission(false);
    setSummaryPollPausedForPermission(false);
    assessmentsByClientRef.current.clear();
    analysesByClientRef.current.clear();
    detailInflightRef.current.clear();
    setDetailVersion((v) => v + 1);
    if (clientsRef.current.length === 0) {
      setLoading(true);
      setSummaryLoading(true);
    }
    subscribedRef.current = false;
    summarySubscribedRef.current = false;
    setRefreshTick((t) => t + 1);
  }, []);

  return (
    <ClientsCacheContext.Provider
      value={{
        clients,
        clientsFetchError,
        loading,
        clientSummaries,
        summaryLoading,
        summaryFetchError,
        getClientDetail,
        detailVersion,
        refresh,
      }}
    >
      {children}
    </ClientsCacheContext.Provider>
  );
}

export function useClientDetail(clientId: string | null | undefined): Required<ClientDetail> {
  const { getClientDetail, detailVersion } = useClientsCache();
  const [assessments, setAssessments] = useState<CachedAssessment[]>([]);
  const [postureAnalyses, setPostureAnalyses] = useState<CachedPostureAnalysis[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientId) {
      setAssessments([]);
      setPostureAnalyses([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void getClientDetail(clientId).then((detail) => {
      if (cancelled) return;
      setAssessments(detail.assessments);
      setPostureAnalyses(detail.postureAnalyses);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [clientId, getClientDetail, detailVersion]);

  return { assessments, postureAnalyses, loading };
}

export function useClientsCache(): ClientsCacheContextType {
  const ctx = useContext(ClientsCacheContext);
  if (!ctx) {
    throw new Error('useClientsCache must be used within a ClientsCacheProvider');
  }
  return ctx;
}

/**
 * Non-throwing variant for components (e.g. ClientFormSheet) that render both
 * inside the route-scoped cache (clients list) and outside it (client detail /
 * edit pages). Returns undefined when no provider is mounted; callers should
 * treat cache side-effects like refresh() as optional in that case.
 */
export function useClientsCacheOptional(): ClientsCacheContextType | undefined {
  return useContext(ClientsCacheContext);
}
